import React, { useState, useRef, useEffect } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';

const PhotoGallery = ({ photos }) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  
  // Zoom functionality state
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  
  const imageContainerRef = useRef(null);
  const touchStartDistance = useRef(0);

  // Reset zoom when changing images
  useEffect(() => {
    if (selectedIndex !== undefined) {
      setScale(1);
      setPosition({ x: 0, y: 0 });
    }
  }, [selectedIndex]);

  // Keyboard navigation
  useEffect(() => {
    if (!isLightboxOpen) return;

    const handleKeyDown = (e) => {
      if (e?.key === 'Escape') closeLightbox();
      if (e?.key === 'ArrowLeft') handlePrevious();
      if (e?.key === 'ArrowRight') handleNext();
      if (e?.key === '+' || e?.key === '=') handleZoomIn();
      if (e?.key === '-' || e?.key === '_') handleZoomOut();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isLightboxOpen, selectedIndex]);

  const handlePrevious = () => {
    setImageLoading(true);
    setSelectedIndex((prev) => (prev === 0 ? photos?.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setImageLoading(true);
    setSelectedIndex((prev) => (prev === photos?.length - 1 ? 0 : prev + 1));
  };

  const handleThumbnailClick = (index) => {
    setImageLoading(true);
    setSelectedIndex(index);
  };

  const openLightbox = () => {
    setIsLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setIsLightboxOpen(false);
    setScale(1);
    setPosition({ x: 0, y: 0 });
    document.body.style.overflow = 'auto';
  };

  // Zoom controls
  const handleZoomIn = () => {
    setScale((prev) => Math.min(prev + 0.5, 4));
  };

  const handleZoomOut = () => {
    setScale((prev) => {
      const newScale = Math.max(prev - 0.5, 1);
      if (newScale === 1) {
        setPosition({ x: 0, y: 0 });
      }
      return newScale;
    });
  };

  const handleResetZoom = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  };

  // Mouse drag for panning
  const handleMouseDown = (e) => {
    if (scale > 1) {
      setIsDragging(true);
      setDragStart({
        x: e?.clientX - position?.x,
        y: e?.clientY - position?.y,
      });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && scale > 1) {
      setPosition({
        x: e?.clientX - dragStart?.x,
        y: e?.clientY - dragStart?.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Touch pinch-to-zoom
  const getTouchDistance = (touches) => {
    const dx = touches?.[0]?.clientX - touches?.[1]?.clientX;
    const dy = touches?.[0]?.clientY - touches?.[1]?.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const handleTouchStart = (e) => {
    if (e?.touches?.length === 2) {
      touchStartDistance.current = getTouchDistance(e?.touches);
    }
  };

  const handleTouchMove = (e) => {
    if (e?.touches?.length === 2) {
      e?.preventDefault();
      const currentDistance = getTouchDistance(e?.touches);
      const scaleChange = currentDistance / touchStartDistance?.current;
      setScale((prev) => Math.min(Math.max(prev * scaleChange, 1), 4));
      touchStartDistance.current = currentDistance;
    }
  };

  // Mouse wheel zoom
  const handleWheel = (e) => {
    if (isLightboxOpen) {
      e?.preventDefault();
      const delta = e?.deltaY > 0 ? -0.2 : 0.2;
      setScale((prev) => Math.min(Math.max(prev + delta, 1), 4));
    }
  };

  if (!photos || photos?.length === 0) {
    return (
      <div className="bg-card rounded-xl md:rounded-2xl p-8 md:p-12 border border-border">
        <div className="flex flex-col items-center justify-center text-center space-y-4">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
            <Icon name="Image" size={32} className="text-muted-foreground" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              Henüz Fotoğraf Eklenmedi
            </h3>
            <p className="text-sm text-muted-foreground">
              Bu etkinlik için henüz fotoğraf yüklenmemiş
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Main Gallery */}
      <div className="bg-card rounded-xl md:rounded-2xl overflow-hidden border border-border shadow-sm">
        {/* Header */}
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Icon name="Image" size={20} className="text-primary" />
            </div>
            <div>
              <h3 className="text-lg md:text-xl font-semibold text-foreground">
                Etkinlik Görselleri
              </h3>
              <p className="text-sm text-muted-foreground">
                {photos?.length} fotoğraf
              </p>
            </div>
          </div>
          <button
            onClick={openLightbox}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-muted hover:bg-muted/80 transition-colors text-sm font-medium"
          >
            <Icon name="Maximize2" size={16} />
            <span className="hidden sm:inline">Tam Ekran</span>
          </button>
        </div>

        {/* Main Image Viewer */}
        <div className="relative bg-black/5 aspect-[16/9] md:aspect-[21/9] group">
          {/* Loading Skeleton */}
          {imageLoading && (
            <div className="absolute inset-0 bg-muted animate-pulse" />
          )}
          
          {/* Main Image with lazy loading */}
          <Image
            src={photos?.[selectedIndex]?.url}
            alt={photos?.[selectedIndex]?.alt || `Etkinlik görseli ${selectedIndex + 1}`}
            className="w-full h-full object-cover"
            onLoad={() => setImageLoading(false)}
            lazy={false}
          />

          {/* Navigation Arrows */}
          {photos?.length > 1 && (
            <>
              <button
                onClick={handlePrevious}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-black/50 hover:bg-black/70 backdrop-blur-sm flex items-center justify-center text-white transition-all opacity-0 group-hover:opacity-100"
                aria-label="Önceki fotoğraf"
              >
                <Icon name="ChevronLeft" size={24} />
              </button>
              <button
                onClick={handleNext}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-black/50 hover:bg-black/70 backdrop-blur-sm flex items-center justify-center text-white transition-all opacity-0 group-hover:opacity-100"
                aria-label="Sonraki fotoğraf"
              >
                <Icon name="ChevronRight" size={24} />
              </button>
            </>
          )}

          {/* Image Counter */}
          <div className="absolute bottom-4 right-4 px-3 py-1.5 rounded-full bg-black/50 backdrop-blur-sm text-white text-sm font-medium">
            {selectedIndex + 1} / {photos?.length}
          </div>
        </div>

        {/* Thumbnail Grid with lazy loading */}
        <div className="p-4 md:p-6">
          <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-2 md:gap-3">
            {photos?.map((photo, index) => (
              <button
                key={photo?.id || index}
                onClick={() => handleThumbnailClick(index)}
                className={`relative aspect-square rounded-lg overflow-hidden transition-all duration-200 ${
                  selectedIndex === index
                    ? 'ring-2 ring-primary scale-95 shadow-lg'
                    : 'ring-1 ring-border hover:ring-2 hover:ring-primary/50 hover:scale-105 shadow-sm'
                }`}
                aria-label={`Fotoğraf ${index + 1}'i görüntüle`}
              >
                <Image
                  src={photo?.url}
                  alt={photo?.alt || `Thumbnail ${index + 1}`}
                  className="w-full h-full object-cover"
                  lazy={true}
                />
                {selectedIndex === index && (
                  <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                    <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                      <Icon name="Check" size={16} className="text-white" />
                    </div>
                  </div>
                )}
                <div className="absolute inset-0 bg-black/0 hover:bg-black/10 transition-colors" />
              </button>
            ))}
          </div>
        </div>
      </div>
      {/* Enhanced Lightbox Modal with Zoom */}
      {isLightboxOpen && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 backdrop-blur-sm flex items-center justify-center p-4"
          onWheel={handleWheel}
        >
          {/* Close Button */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors z-10"
            aria-label="Kapat"
          >
            <Icon name="X" size={24} />
          </button>

          {/* Zoom Controls */}
          <div className="absolute top-4 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 z-10">
            <button
              onClick={handleZoomOut}
              disabled={scale <= 1}
              className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-white transition-colors"
              aria-label="Zoom out"
            >
              <Icon name="ZoomOut" size={18} />
            </button>
            <span className="text-white text-sm font-medium min-w-[60px] text-center">
              {Math.round(scale * 100)}%
            </span>
            <button
              onClick={handleZoomIn}
              disabled={scale >= 4}
              className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-white transition-colors"
              aria-label="Zoom in"
            >
              <Icon name="ZoomIn" size={18} />
            </button>
            {scale > 1 && (
              <button
                onClick={handleResetZoom}
                className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors ml-2"
                aria-label="Reset zoom"
              >
                <Icon name="Maximize2" size={18} />
              </button>
            )}
          </div>

          {/* Image Counter */}
          <div className="absolute top-4 left-4 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm text-white text-sm font-medium z-10">
            {selectedIndex + 1} / {photos?.length}
          </div>

          {/* Lightbox Image with Zoom and Pan */}
          <div 
            ref={imageContainerRef}
            className="relative max-w-7xl max-h-[90vh] w-full overflow-hidden"
            style={{ cursor: scale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default' }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
          >
            <div
              style={{
                transform: `scale(${scale}) translate(${position?.x / scale}px, ${position?.y / scale}px)`,
                transition: isDragging ? 'none' : 'transform 0.2s ease-out',
                transformOrigin: 'center center',
              }}
            >
              <Image
                src={photos?.[selectedIndex]?.url}
                alt={photos?.[selectedIndex]?.alt || `Etkinlik görseli ${selectedIndex + 1}`}
                className="w-full h-full object-contain select-none"
                lazy={false}
                draggable={false}
              />
            </div>
          </div>

          {/* Navigation Arrows */}
          {photos?.length > 1 && (
            <>
              <button
                onClick={handlePrevious}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors z-10"
                aria-label="Önceki fotoğraf"
              >
                <Icon name="ChevronLeft" size={28} />
              </button>
              <button
                onClick={handleNext}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors z-10"
                aria-label="Sonraki fotoğraf"
              >
                <Icon name="ChevronRight" size={28} />
              </button>
            </>
          )}

          {/* Thumbnail Strip with lazy loading */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 max-w-4xl w-full px-4 z-10">
            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
              {photos?.map((photo, index) => (
                <button
                  key={photo?.id || index}
                  onClick={() => handleThumbnailClick(index)}
                  className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden transition-all ${
                    selectedIndex === index
                      ? 'ring-2 ring-white scale-110' :'ring-1 ring-white/30 opacity-60 hover:opacity-100'
                  }`}
                >
                  <Image
                    src={photo?.url}
                    alt={photo?.alt || `Thumbnail ${index + 1}`}
                    className="w-full h-full object-cover"
                    lazy={true}
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Helper text */}
          {scale > 1 && (
            <div className="absolute bottom-20 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm text-white text-xs z-10">
              Sürükleyerek gezinin • Fare tekerleği ile zoom yapın
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default PhotoGallery;